import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

const REASONS = [
  {
    value: 'on_leave',
    label: 'On leave',
    help: 'Temporary. RM gets a follow-up on the date you set.',
  },
  {
    value: 'batch_change',
    label: 'Change of batch',
    help: 'Moving to another batch. No RM leave follow-up.',
  },
  {
    value: 'left_permanently',
    label: 'Leaving forever',
    help: 'Not returning to this batch. No RM leave follow-up.',
  },
];

export default function StudentLeaveUnassignDialog({
  open,
  studentName = '',
  batchName = '',
  submitting = false,
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason('');
    setFollowUpDate('');
    setNotes('');
    setError('');
  }, [open]);

  const handleConfirm = async () => {
    if (!reason) {
      setError('Select why this student is being unassigned');
      return;
    }
    if (reason === 'on_leave' && !followUpDate) {
      setError('Follow-up date is required when the student is on leave');
      return;
    }
    setError('');
    try {
      await onConfirm({
        reason,
        follow_up_date: reason === 'on_leave' ? followUpDate : undefined,
        notes: notes.trim(),
      });
    } catch {
      /* parent shows the error */
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Unassign student</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {studentName || 'This student'} will be removed from
          {batchName ? ` ${batchName}` : ' the batch'}.
        </Typography>
        <FormControl error={Boolean(error) && !reason} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Reason
          </Typography>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={<Radio />}
                label={
                  <span>
                    {item.label}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {item.help}
                    </Typography>
                  </span>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
        {reason === 'on_leave' ? (
          <TextField
            required
            fullWidth
            type="date"
            label="Follow-up date"
            value={followUpDate}
            onChange={(e) => setFollowUpDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="RM sees this on Leave follow-ups and is alerted on this date."
            sx={{ mb: 2 }}
          />
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Note (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error ? (
          <FormHelperText error sx={{ mt: 1 }}>
            {error}
          </FormHelperText>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Unassigning…' : 'Unassign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
